import {useState, SyntheticEvent} from 'react';
import Box from '@mui/material/Box';
import {
  Typography,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import Footer from '../components/Footer';
import CloseCircleIcon from '$assets/svgs/close-circle.svg?react';
import {useNavigate} from 'react-router-dom';
import {RoutePath} from '$constants/routes';

import BookingButton from '../components/BookingButton';

import {reviewCancelSummaryViewStyles} from '../styles';
import {
  BookingView,
  BookingDataUpdater,
  AppointmentDetailNavigatePayload,
  CancelAppointmentChoices,
} from '../types';
import {dateToDDMMYYYY, formatTimeAMPM} from '$utils/date';

import {cancelAppointment, CancelAppointmentVariables} from '$api/bookingFlow';

import {AxiosError} from 'axios';
import CancelImage from '../../../assets/svgs/cancel-icon.svg';
import CancelImageBackground from '../../../assets/svgs/cancel-icon-background.svg';
import PaymentSummaryBox from '../components/PaymentSummaryBox';
import {REVIEW_CANCEL_TEXTS} from '../constant';

interface CancellationReasonWithId {
  reasonId: number;
  reasonName: string;
  otherReason?: string;
}

interface CancellationReasonWithFlags {
  consult: boolean;
  reschedule: boolean;
  provider: boolean;
  other: boolean;
  otherReason?: string;
}

function isCancellationReasonWithId(
  reason: CancellationReasonWithId | CancellationReasonWithFlags
): reason is CancellationReasonWithId {
  return 'reasonName' in reason;
}

interface ReviewCancelSummaryViewProps {
  setActiveView: (view: BookingView) => void;
  setBookingData: BookingDataUpdater;
  bookingChoices: CancelAppointmentChoices;
}

function ReviewCancelSummaryView({
  setActiveView,
  bookingChoices,
}: ReviewCancelSummaryViewProps) {
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const dateString = bookingChoices.date
    ? dateToDDMMYYYY(bookingChoices.date)
    : REVIEW_CANCEL_TEXTS.NOT_SPECIFIED;

  const consultantName =
    bookingChoices.consultant?.fullName || REVIEW_CANCEL_TEXTS.NOT_SPECIFIED;

  const totalAmount =
    bookingChoices.consultant?.priceLists?.consultationFee || 0;

  const Time = bookingChoices.slot?.startTime
    ? formatTimeAMPM(bookingChoices.slot.startTime)
    : REVIEW_CANCEL_TEXTS.NOT_SPECIFIED;

  const handleBackClick = () => {
    setActiveView(BookingView.CancelFormView);
  };

  const handleCloseError = (
    _event?: SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowError(false);
  };

  const mapCancellationReasonToParams = (
    appointmentId: number,
    reasons: CancellationReasonWithId | CancellationReasonWithFlags
  ): CancelAppointmentVariables => {
    if ('reasonName' in reasons) {
      const params: CancelAppointmentVariables = {
        id: appointmentId,
        cancellationReason: reasons.reasonId,
        payment_id: bookingChoices.payment_id,
      };

      if (reasons.otherReason) {
        params.otherCancellationReason = reasons.otherReason;
      }

      return params;
    } else {
      throw new Error('Legacy cancellation reason format is not supported');
    }
  };

  const handleContinueClick = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      if (!bookingChoices.appointmentId) {
        throw new Error(REVIEW_CANCEL_TEXTS.ERROR_MISSING_ID);
      }

      if (!bookingChoices.cancelReasons) {
        throw new Error(REVIEW_CANCEL_TEXTS.ERROR_MISSING_REASON);
      }

      const cancelParams = mapCancellationReasonToParams(
        bookingChoices.appointmentId,
        bookingChoices.cancelReasons
      );

      const hasOtherReason = bookingChoices.cancelReasons
        ? isCancellationReasonWithId(bookingChoices.cancelReasons)
          ? bookingChoices.cancelReasons.reasonName ===
            REVIEW_CANCEL_TEXTS.OTHER
          : false
        : false;

      if (hasOtherReason && !cancelParams.otherCancellationReason) {
        setError(REVIEW_CANCEL_TEXTS.ERROR_OTHER_REASON_REQUIRED);
        setShowError(true);
        setIsSubmitting(false);
        return;
      }

      await cancelAppointment(cancelParams);

      const appointmentData: AppointmentDetailNavigatePayload = {
        careType:
          bookingChoices.care?.title || REVIEW_CANCEL_TEXTS.NOT_SPECIFIED,
        date: bookingChoices.date || new Date(),
      };

      navigate(RoutePath.deleteSuccessRoute, {
        state: appointmentData,
      });
    } catch (error) {
      console.error(REVIEW_CANCEL_TEXTS.ERROR_LOG_PREFIX, error);

      const err = error as AxiosError;
      const errorResponse = err?.response?.data as {errors?: string[]};

      if (
        errorResponse?.errors?.includes(
          "Other cancellation reason can't be blank"
        )
      ) {
        setError(REVIEW_CANCEL_TEXTS.ERROR_OTHER_REASON_REQUIRED);
      } else if (
        err?.response?.status === 401 ||
        err?.response?.status === 403
      ) {
        setError(REVIEW_CANCEL_TEXTS.ERROR_UNAUTHORIZED);
      } else if (err?.response?.status === 404) {
        setError(REVIEW_CANCEL_TEXTS.ERROR_NOT_FOUND);
      } else if (err?.response?.status === 400) {
        setError(REVIEW_CANCEL_TEXTS.ERROR_BAD_REQUEST);
      } else if (err?.message) {
        setError(`${REVIEW_CANCEL_TEXTS.ERROR_LOG_PREFIX} ${err.message}`);
      } else {
        setError(REVIEW_CANCEL_TEXTS.ERROR_GENERIC);
      }

      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {' '}
      <Box sx={reviewCancelSummaryViewStyles.wrapper}>
        <Box sx={reviewCancelSummaryViewStyles.container}>
          <Snackbar
            open={showError}
            autoHideDuration={6000}
            onClose={handleCloseError}
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
          >
            <Alert
              onClose={handleCloseError}
              severity="error"
              sx={reviewCancelSummaryViewStyles.alertStyle}
            >
              {error || REVIEW_CANCEL_TEXTS.ERROR_DEFAULT}
            </Alert>
          </Snackbar>
          <Box sx={reviewCancelSummaryViewStyles.imageContainer}>
            <Box
              component="img"
              src={CancelImageBackground}
              alt={REVIEW_CANCEL_TEXTS.CANCEL_BACKGROUND_ALT}
              sx={reviewCancelSummaryViewStyles.backgroundImage}
            />
            <Box
              component="img"
              src={CancelImage}
              alt={REVIEW_CANCEL_TEXTS.CANCEL_ICON_ALT}
              sx={reviewCancelSummaryViewStyles.cancelIcon}
            />
          </Box>
          <Typography variant="h5" sx={reviewCancelSummaryViewStyles.titleText}>
            {REVIEW_CANCEL_TEXTS.TITLE}
          </Typography>
          <Typography
            variant="body1"
            sx={reviewCancelSummaryViewStyles.descriptionText}
          >
            {REVIEW_CANCEL_TEXTS.DESCRIPTION}
          </Typography>

          <Typography
            variant="h6"
            sx={reviewCancelSummaryViewStyles.summaryTitle}
            gutterBottom
          >
            {REVIEW_CANCEL_TEXTS.SUMMARY_TITLE}
          </Typography>
          <Box sx={reviewCancelSummaryViewStyles.paymentSummaryBox}>
            <PaymentSummaryBox
              totalAmount={totalAmount}
              items={[
                {
                  label: 'Surgery consultation',
                  amount: totalAmount,
                },
              ]}
              currency="£"
              headerText="Total Paid"
              width="100%"
            />
          </Box>

          <Box sx={reviewCancelSummaryViewStyles.summaryContainer}>
            <Box sx={reviewCancelSummaryViewStyles.spacing}>
              <Stack
                spacing={3}
                sx={reviewCancelSummaryViewStyles.summaryStack}
                textAlign="left"
              >
                <Stack spacing={0.5}>
                  <Typography
                    variant="body1"
                    sx={reviewCancelSummaryViewStyles.primaryText}
                  >
                    {REVIEW_CANCEL_TEXTS.LABEL_APPOINTMENT}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={reviewCancelSummaryViewStyles.secondaryText}
                  >
                    {bookingChoices.care?.title ||
                      REVIEW_CANCEL_TEXTS.NOT_SPECIFIED}
                  </Typography>
                </Stack>

                <Stack spacing={0.5}>
                  <Typography
                    variant="body1"
                    sx={reviewCancelSummaryViewStyles.primaryText}
                  >
                    {REVIEW_CANCEL_TEXTS.LABEL_CONSULTANT}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={reviewCancelSummaryViewStyles.secondaryText}
                  >
                    {consultantName}
                  </Typography>
                </Stack>

                <Stack spacing={0.5}>
                  <Typography
                    variant="body1"
                    sx={reviewCancelSummaryViewStyles.primaryText}
                  >
                    {REVIEW_CANCEL_TEXTS.LABEL_DATE}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={reviewCancelSummaryViewStyles.secondaryText}
                  >
                    {dateString}
                  </Typography>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography
                    variant="body1"
                    sx={reviewCancelSummaryViewStyles.primaryText}
                  >
                    {REVIEW_CANCEL_TEXTS.LABEL_TIME}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={reviewCancelSummaryViewStyles.secondaryText}
                  >
                    {Time}
                  </Typography>
                </Stack>

                <Divider sx={reviewCancelSummaryViewStyles.dividerStyle} />
              </Stack>
            </Box>
          </Box>

          <Box sx={reviewCancelSummaryViewStyles.buttonRow}>
            <Box sx={reviewCancelSummaryViewStyles.leftButtonBox}>
              <Box sx={reviewCancelSummaryViewStyles.fullWidthBox}>
                <BookingButton
                  variant="secondary"
                  disabled={isSubmitting}
                  onClick={handleBackClick}
                  testId="cancel-button"
                >
                  {REVIEW_CANCEL_TEXTS.BUTTON_CANCEL}
                </BookingButton>
              </Box>
            </Box>
            <Box sx={reviewCancelSummaryViewStyles.rightButtonBox}>
              <Box sx={reviewCancelSummaryViewStyles.fullWidthBox}>
                <BookingButton
                  disabled={isSubmitting}
                  onClick={handleContinueClick}
                  endIcon={
                    isSubmitting ? (
                      <CircularProgress
                        size="1rem"
                        sx={reviewCancelSummaryViewStyles.loadingIcon}
                      />
                    ) : (
                      <CloseCircleIcon />
                    )
                  }
                  testId="confirm-cancellation-button"
                >
                  <Typography noWrap>
                    {REVIEW_CANCEL_TEXTS.BUTTON_CONFIRM}
                  </Typography>
                </BookingButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={reviewCancelSummaryViewStyles.footer}>
        <Footer />
      </Box>
    </>
  );
}

export default ReviewCancelSummaryView;
